import React from 'react';
import { TooltipRenderProps } from 'react-joyride';
import {
  Box,
  Paper,
  Typography,
  Button,
  Chip,
  LinearProgress,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { TOTAL_TOUR_STEPS } from './tourSteps';

interface TourStepProps extends TooltipRenderProps {
  stepNumber?: number;
}

export const TourStep: React.FC<TourStepProps> = ({
  continuous,
  index,
  step,
  backProps,
  primaryProps,
  skipProps,
  tooltipProps,
  stepNumber,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const currentStep = stepNumber || index + 1;
  const progress = (currentStep / TOTAL_TOUR_STEPS) * 100;

  return (
    <Paper
      {...tooltipProps}
      elevation={8}
      sx={{
        maxWidth: isMobile ? 320 : 400,
        borderRadius: 2,
        overflow: 'hidden',
      }}
    >
      {/* Progress Bar */}
      <LinearProgress
        variant="determinate"
        value={progress}
        sx={{
          height: 6,
          bgcolor: 'grey.200',
          '& .MuiLinearProgress-bar': {
            bgcolor: theme.palette.primary.main,
          },
        }}
      />

      {/* Content */}
      <Box sx={{ p: 3 }}>
        {/* Step Indicator */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Chip
            label={`Step ${currentStep} of ${TOTAL_TOUR_STEPS}`}
            size="small"
            color="primary"
            sx={{ fontWeight: 600 }}
          />
        </Box>

        {/* Title */}
        {step.title && (
          <Typography
            variant="h6"
            fontWeight={700}
            sx={{ mb: 1.5, color: '#111827' }}
          >
            {step.title}
          </Typography>
        )}

        {/* Description */}
        <Typography
          variant="body1"
          sx={{ mb: 3, color: 'text.secondary', lineHeight: 1.6 }}
        >
          {step.content}
        </Typography>

        {/* Action Buttons */}
        <Box
          sx={{
            display: 'flex',
            gap: 1.5,
            flexDirection: isMobile ? 'column' : 'row',
            alignItems: 'stretch',
          }}
        >
          {/* Skip Tour Button */}
          <Button
            {...skipProps}
            size="small"
            sx={{
              color: 'text.secondary',
              fontWeight: 500,
              '&:hover': {
                bgcolor: 'grey.100',
              },
            }}
          >
            Skip Tour
          </Button>

          <Box sx={{ flex: 1 }} />

          {/* Back Button */}
          {index > 0 && (
            <Button
              {...backProps}
              variant="outlined"
              size="medium"
              sx={{ fontWeight: 600 }}
            >
              Back
            </Button>
          )}

          {/* Next/Finish Button */}
          <Button
            {...primaryProps}
            variant="contained"
            size="medium"
            sx={{
              fontWeight: 600,
              minWidth: 100,
              bgcolor: theme.palette.primary.main,
              '&:hover': {
                bgcolor: theme.palette.primary.dark,
              },
            }}
          >
            {continuous ? 'Next' : 'Finish'}
          </Button>
        </Box>
      </Box>
    </Paper>
  );
};
